"use client"

import { useState, useEffect } from 'react'
import { useApi } from '@/lib/api'
import { useCodeValues } from '@/hooks/use-code-values'
import { useFormatters } from '@/hooks/use-formatters'
import { saveAs } from 'file-saver'
import { normalizeUTCDateOnlyToLocal } from '@/lib/utils'

// Transaction data interface - Pure API model
export interface Transaction {
  TransactionId: number               // Transaction Header Id
  TransactionNumber: string           // Transaction reference number
  TransactionTypeId: number           // Transaction type ID (needs code lookup)
  TransactionDate: string | null      // ISO date string (nullable)
  WorkerName: string                  // Name of the worker
  PayeeName: string                   // Name of the payee
  AlternateBillingClient: string | null // Alternate billing client name (nullable)
  FromDate: string | null             // Start date (ISO string, nullable)
  ToDate: string | null               // End date (ISO string, nullable)
  BillSubtotal: number                // Subtotal billed amount
  PaymentSubtotal: number             // Subtotal payment amount
  TransactionHeaderStatusId: number   // Status ID (needs code lookup)
}

// Type-safe field selection based on Transaction model
type TransactionField = keyof Transaction

// Default fields to select for transaction reports
const DEFAULT_TRANSACTION_FIELDS: TransactionField[] = [
  'TransactionId',
  'TransactionNumber', 
  'TransactionTypeId',
  'TransactionDate',
  'WorkerName',
  'PayeeName',
  'AlternateBillingClient',
  'FromDate',
  'ToDate',
  'BillSubtotal',
  'PaymentSubtotal',
  'TransactionHeaderStatusId'
]

// Reports API types based on the Swagger specification
export interface ReportSearchParams {
  filter?: string
  orderBy?: string
  top?: number
  skip?: number
  search?: string
  facets?: string[]
}

export interface ReportResponse<T> {
  value: T[]
  '@odata.count'?: number
  '@odata.nextLink'?: string
  facets?: Record<string, any>
}

const TRANSACTION_REPORT_TYPE_ID = 16
const MAX_RECORDS_PER_REQUEST = 1000

export interface UseTransactionReportsOptions {
  engagementId?: number
  autoFetch?: boolean
  searchParams?: ReportSearchParams
  currentPage?: number
  pageSize?: number
}

export interface UseTransactionReportsResult {
  transactions: Transaction[]
  loading: boolean
  error: string | null
  totalCount: number
  isExporting: boolean
  exportError: string | null
  uniqueWorkerNames: string[]
  workerNamesLoading: boolean
  refetch: () => Promise<void>
  exportToCSV: (searchParams?: ReportSearchParams) => Promise<void>
}

export function useTransactionReports(options: UseTransactionReportsOptions = {}): UseTransactionReportsResult {
  const { engagementId, autoFetch = true, searchParams = {}, currentPage = 1, pageSize = 10 } = options
  const { reportsApi } = useApi()
  const { getCodeValueText } = useCodeValues()
  const { formatCurrency, formatDate } = useFormatters()
  
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [uniqueWorkerNames, setUniqueWorkerNames] = useState<string[]>([])
  const [workerNamesLoading, setWorkerNamesLoading] = useState(false)

  const buildQueryParams = (params: ReportSearchParams): URLSearchParams => {
    const queryParams = new URLSearchParams()
    
    // Always include our type-safe field selection
    queryParams.append('$select', DEFAULT_TRANSACTION_FIELDS.join(','))
    
    if (params.filter) queryParams.append('$filter', params.filter)
    if (params.orderBy) queryParams.append('$orderby', params.orderBy)
    if (params.top) queryParams.append('$top', params.top.toString())
    if (params.skip) queryParams.append('$skip', params.skip.toString())
    if (params.search) queryParams.append('$search', params.search)
    if (params.facets?.length) {
      params.facets.forEach(facet => queryParams.append('facets', facet))
    }
    
    return queryParams
  }

  const buildFilterWithEngagement = (baseFilter: string = ''): string => {
    let filter = baseFilter
    if (engagementId) {
      const engagementFilter = `EngagementId eq ${engagementId}`
      filter = filter ? `${filter} and ${engagementFilter}` : engagementFilter
    }
    return filter
  }

  const makeReportApiCall = async (params: ReportSearchParams): Promise<ReportResponse<Transaction>> => {
    if (!reportsApi) {
      throw new Error('Reports API not available')
    }

    const queryParams = buildQueryParams(params)
    const path = `Report/${TRANSACTION_REPORT_TYPE_ID}?${queryParams.toString()}`
    return await reportsApi.get<ReportResponse<Transaction>>(path)
  }

  const fetchTransactions = async (params: ReportSearchParams = searchParams): Promise<void> => {
    try {
      setLoading(true)
      setError(null)

      // Build filter with engagement
      const filter = buildFilterWithEngagement(params.filter)

      // Calculate skip based on pagination
      const skip = (currentPage - 1) * pageSize
      const finalParams = { ...params, filter, top: pageSize, skip }

      const response = await makeReportApiCall(finalParams)

      // Transform date strings from UTC format to local format
      const transformedTransactions = (response.value || []).map(transaction => ({
        ...transaction,
        TransactionDate: normalizeUTCDateOnlyToLocal(transaction.TransactionDate),
        FromDate: normalizeUTCDateOnlyToLocal(transaction.FromDate),
        ToDate: normalizeUTCDateOnlyToLocal(transaction.ToDate)
      }))

      setTransactions(transformedTransactions)
      setTotalCount(response['@odata.count'] || response.value?.length || 0)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch transactions'
      setError(errorMessage)
      console.error('Transaction reports fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchAllTransactions = async (params: ReportSearchParams = {}): Promise<Transaction[]> => {
    const allTransactions: Transaction[] = []
    let skip = 0
    let hasMoreData = true

    // Build filter with engagement
    const filter = buildFilterWithEngagement(params.filter)
    const finalParams = { ...params, filter }

    while (hasMoreData) {
      const batchParams = { ...finalParams, top: MAX_RECORDS_PER_REQUEST, skip }
      const response = await makeReportApiCall(batchParams)
      const batchTransactions = response.value || []
      
      // Transform date strings from UTC format to local format
      const transformedBatch = batchTransactions.map(transaction => ({
        ...transaction,
        TransactionDate: normalizeUTCDateOnlyToLocal(transaction.TransactionDate),
        FromDate: normalizeUTCDateOnlyToLocal(transaction.FromDate),
        ToDate: normalizeUTCDateOnlyToLocal(transaction.ToDate)
      }))
      
      allTransactions.push(...transformedBatch)
      
      // Check if we have more data
      hasMoreData = batchTransactions.length === MAX_RECORDS_PER_REQUEST
      skip += MAX_RECORDS_PER_REQUEST
    }

    return allTransactions
  }

  const transformTransactionToCSVRow = (transaction: Transaction): string[] => {
    return [
      transaction.TransactionId.toString(),
      `"${transaction.TransactionNumber}"`,
      `"${getCodeValueText(transaction.TransactionHeaderStatusId, 'trn.CodeTransactionHeaderStatus')}"`,
      `"${getCodeValueText(transaction.TransactionTypeId, 'trn.CodeTransactionType')}"`,
      `"${formatDate(transaction.TransactionDate, '-')}"`,
      `"${transaction.WorkerName}"`,
      `"${transaction.PayeeName}"`,
      `"${transaction.AlternateBillingClient || ''}"`,
      `"${formatDate(transaction.FromDate, '-')}"`,
      `"${formatDate(transaction.ToDate, '-')}"`,
      `"${formatCurrency(transaction.BillSubtotal)}"`,
      `"${formatCurrency(transaction.PaymentSubtotal)}"`
    ]
  }

  const generateCSV = (transactions: Transaction[]): string => {
    const headers = [
      'ID',
      'Transaction #',
      'Status',
      'Type', 
      'Date',
      'Worker Name',
      'Payee Name',
      'Alternate Bill',
      'From',
      'To',
      'Gross Bill',
      'Gross/Reported Pay'
    ]

    const csvRows = [
      headers.join(','), // Header row
      ...transactions.map(transaction => transformTransactionToCSVRow(transaction).join(','))
    ]

    return csvRows.join('\n')
  }

  const generateFileName = (): string => {
    const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD format
    const engagementPart = engagementId ? `engagement-${engagementId}-` : ''
    return `${engagementPart}transactions-${today}.csv`
  }

  const exportToCSV = async (exportParams: ReportSearchParams = searchParams): Promise<void> => {
    try {
      setIsExporting(true)
      setExportError(null)

      // Fetch all transactions
      const allTransactions = await fetchAllTransactions(exportParams)
      
      if (allTransactions.length === 0) {
        throw new Error('No transactions found to export')
      }

      // Generate CSV content
      const csvContent = generateCSV(allTransactions)
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const fileName = generateFileName()
      
      saveAs(blob, fileName)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export transactions'
      setExportError(errorMessage)
      console.error('Transaction export error:', err)
    } finally {
      setIsExporting(false)
    }
  }

  const fetchUniqueWorkerNames = async (): Promise<void> => {
    try {
      setWorkerNamesLoading(true)
      setUniqueWorkerNames([])
      const allTransactions = await fetchAllTransactions({})
      const names = [...new Set(allTransactions.map(t => t.WorkerName).filter(Boolean))].sort()
      setUniqueWorkerNames(names)
    } catch (err) {
      console.error('Failed to fetch unique worker names:', err)
    } finally {
      setWorkerNamesLoading(false)
    }
  }

  useEffect(() => {
    if (!engagementId) {
      setUniqueWorkerNames([])
      return
    }
    fetchUniqueWorkerNames()
  }, [engagementId])

  const refetch = async (): Promise<void> => {
    await fetchTransactions(searchParams)
  }

  useEffect(() => {
    if (autoFetch) {
      fetchTransactions(searchParams)
    }
  }, [engagementId, autoFetch, currentPage, pageSize, searchParams]) // Re-fetch when engagement, page, page size, or search params change

  return {
    transactions,
    loading,
    error,
    totalCount,
    isExporting,
    exportError,
    uniqueWorkerNames,
    workerNamesLoading,
    refetch,
    exportToCSV
  }
}